import {useEffect, useRef, useState} from "react";
import {Stripe} from "@stripe/stripe-js"
import {
    Elements,
} from '@stripe/react-stripe-js';
import './index.scss';
import {LocalizerHelper} from "../../helpers/localizerHelper";
import {BILL_STATUS, DITS_STATUS} from "../../constants/data";
import {useNavigate, useParams} from "react-router";
import RestaurantHeader from "../restaurant/RestaurantHeader";
import {formatPrice} from "../../helpers/formatHelper";
import { useSelector } from "react-redux";
import StripeCheckoutForm from "./StripeCheckoutForm";
import { RootState } from "../../store";
import { LoaderGif } from "../loader";
import { BillModel } from "../../models/bill";
import Screen from '../screen';
import { createPaymentTransaction } from "../../helpers/apiHelper";
import { ELECTRONIC_PAYMENT_TYPE, IntentPayment, PaymentIntentData } from "../../models/payments";
import { Button } from "@material-ui/core";
import WalletButton from "./WalletButton";

//TODO HANDLE NEXI

interface PaymentParams {
    stripePromise: Promise<Stripe|null>
}

const Payment = ({stripePromise}: PaymentParams) => {
    const navigate = useNavigate();
    
    const { 
        link_alias: link_alias, 
        restaurant: restaurantId, 
        menu: menuId, 
        dits: sessionId 
    } = useParams<{ restaurant: string; menu: string, link_alias: string, dits: string}>();
    
    const lastLinkAlias = (window.localStorage) ? (window.localStorage.getItem('chuzeat_last_alias_link') ?? '' ) : '';

    const session = useSelector((state: RootState) => state.session.session);
    const bills = useSelector((state: RootState) => state.bill.bills);
    const authorizedBills = useSelector((state: RootState) => state.bill.bills.filter(bill => bill.status === BILL_STATUS.authorized));
    const walletState = useSelector((state: RootState) => state.wallet);
    const walletBalance = useSelector((state: RootState) => state.wallet.wallet?.balance ?? 0);
    const [useWallet, setUseWallet] = useState(false);

    const [paymentIntent, setPaymentIntent] = useState<PaymentIntentData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!!session) {
            if (session.status === DITS_STATUS.paid) {
                const alias = link_alias ?? lastLinkAlias;
                navigate(`/menu/${alias}/thanks/${restaurantId}/${sessionId}/${menuId}`);
            }
        }
    }, [session]);

    const hasTriggeredRef = useRef(false);

    useEffect(() => {
        if (hasTriggeredRef.current) return;
        if (paymentIntent) return;
        if (!walletState.fetched) return;
        const authorizedBills = bills.filter(bill => bill.status === BILL_STATUS.authorized);
        const progressBills = bills.filter(bill => bill.status === BILL_STATUS.processing_payment);

        if(authorizedBills.length > 0){
            if((walletState.wallet?.balance ?? 0) > 0) return;
            hasTriggeredRef.current = true;
            const billsIDs = authorizedBills.map((bill) => bill.id);
            _createPaymentTransaction(billsIDs);
            return;
        }

        if(progressBills.length > 0){
            hasTriggeredRef.current = true;
            const billsIDs = progressBills.map((bill) => bill.id);
            _createPaymentTransaction(billsIDs);
            return;
        }
    }, [walletState, bills]);

    const _createPaymentTransaction = async (billsIDs: string[]) => {
        if(!restaurantId) return;
        setLoading(true);
        try{
            if (billsIDs.length > 0) {
                const result: IntentPayment|null = await createPaymentTransaction({
                    restaurantID: restaurantId,
                    billIDs: billsIDs,
                    wallet: useWallet,
                });
                if(!!result?.error){
                    console.log(result?.error);
                    alert(result?.error);
                }else{
                    if(result?.code === "confirm_payment"){
                        const paymentIntentProgress = result?.processing?.transactions[0]?.data;
                        if(paymentIntentProgress){
                            console.log("££££");
                            console.log(JSON.stringify(paymentIntentProgress));
                            console.log("££££");
                            setPaymentIntent(paymentIntentProgress);
                        }
                        setLoading(false);
                        return;
                    }
                    setPaymentIntent(result?.data ?? null);
                }
            }
        }catch (error){
            console.log(error);
            alert(error);
        }
        setLoading(false);
    }

    const totalAmount = bills.reduce((sum: number, item: BillModel) => (sum + item.amount), 0);
    const paidWalletAmount = bills.reduce((sum: number, item: BillModel) => (sum + item.walletAmount), 0);    

    if(loading) {
        return <LoaderGif 
            image="/assets/loading_check_payment.gif" 
            message={LocalizerHelper.localized('loading_msg_check_payment')} 
        />
    }

    return (
    <Screen className="screen payment_screen page_container">
        <div className="scrollable_content">
            <div>
                <RestaurantHeader className="block" showBack={false} showCart={false}/>
                <div className="container label_total_payment">
                    <div className="price-row">
                        <span className="total-order-price-label">{LocalizerHelper.localized('total_order_price_label')}</span>
                        <span className="total-order-price">{formatPrice(totalAmount)}</span>
                    </div>
                    {paidWalletAmount > 0 && 
                        <div className="price-row">
                            <span className="total-order-price-label">{LocalizerHelper.localized('total_wallet_paid_label')}</span>
                            <span className="total-order-price">{formatPrice(paidWalletAmount)}</span>
                        </div>
                    }
                    {paidWalletAmount > 0 && 
                        <div className="price-row">
                            <span className="total-order-price-label">{LocalizerHelper.localized('total_to_pay')}</span>
                            <span className="total-order-price">{formatPrice(totalAmount - paidWalletAmount)}</span>
                        </div>
                    }
                    
                </div>

                {walletBalance > 0 && authorizedBills.length > 0 && !paymentIntent &&
                    <WalletButton 
                        walletBalance={walletBalance} 
                        totalAmount={totalAmount} 
                        onToggle={setUseWallet} 
                        disabled={!!paymentIntent}
                    />
                }

                {paymentIntent?.type === ELECTRONIC_PAYMENT_TYPE.stripe &&
                    <Elements 
                        stripe={stripePromise} 
                        options={{
                            clientSecret: paymentIntent.clientSecret,
                            customerSessionClientSecret: paymentIntent.customerSessionSecret,
                            customerOptions: {
                                customer: paymentIntent.customerId,
                                ephemeralKey: paymentIntent.ephemeralKey,
                            },
                            appearance: {
                                theme: 'flat',
                                variables: {
                                    colorPrimary: "#fe3436",
                                    borderRadius: '8px',
                                },
                            },
                        }}
                    >
                        <StripeCheckoutForm/>
                    </Elements>
                }

                {paymentIntent && !Object.values(ELECTRONIC_PAYMENT_TYPE).includes(paymentIntent.type) &&
                    <div>{LocalizerHelper.localized("error_no_default_payment_method")}</div>
                }

                {!paymentIntent && authorizedBills.length > 0 && 
                    <Button className="accent-button" 
                        onClick={() => {
                            const billsIDs = authorizedBills.map((bill) => bill.id);
                            _createPaymentTransaction(billsIDs);
                        }} 
                        style={{marginTop: "16px"}}>
                        {LocalizerHelper.localized('proceed_with_new_payment')}
                    </Button>
                }
            </div>
        </div>
    </Screen>
    );
}

export default Payment;
